import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { ETYPE, enemies } from '../entities/enemies';
import { makeEliteMarker } from '../render/gadgets';
import { attachEnemy } from '../render/enemy-instances';
import { SFX } from '../platform/audio';
import { eliteKey, logSeen } from '../platform/storage';
import { banner, radio } from '../ui/hud';
import { P } from '../world/player';
import { spawnEnemy } from './spawn';
/* ============================================================
   精英 —— 這裡才是「Boss 密度」的來源
   ============================================================

   讀 VS 官方 wiki 的關卡表之後改的（`.claude/notes/vs-timeline.md`）。
   關鍵發現：**VS 的「boss」不是關卡王，是每分鐘週期性生成的強化變體** ——
   Glowing Bat、Silver Bat、Giant Werewolf 這些全都是雜兵的巨大化／發光版本。
   Mad Forest 30 分鐘裡有 22 次 boss 生成、分佈在 21 個分鐘，**第一隻在 1:00**。

   所以密度不是靠「多做十幾隻獨立的 Boss」堆出來的（那是十幾套模型與 AI），
   而是靠一個**精英層**：同樣的雜魚，放大、發光、血厚、掉寶箱。

   我方對照（20 分鐘，VS 的 30 分鐘 ×0.667）：
     1:00 起每 60 秒一隻精英，與四隻大型 Boss 的時刻錯開
     → 約 15 隻精英 + 4 隻大型 Boss = 19 次「Boss 級」遭遇，和 VS 的 21 次同量級

   精英與大型 Boss 的分工：
   - **精英**：頻繁、看得懂、打得掉。給的是節奏與寶箱
   - **大型 Boss**：每一幕一次，獨立的模型與 AI。給的是段落感 */

/** 精英的倍率。血量特別高是因為它要撐得住玩家後期的火力，不然還沒看清就死了。

    **`scale` 從 1.7 降到 1.34**（規格 §2.4）。1.7 是「靠放大來辨識」的數字，
    而放大到那個程度會破壞家族剪影的比例 —— 一隻 1.7 倍的 SPIKE 在 20px 下
    會被讀成別的家族。新的辨識靠**共用冠環 + armor 亮一階**，
    那兩者在任何倍率下都成立，所以體型只需要「看得出比旁邊大一點」。 */
/** ⚠️ **2026-08-03（B2）整組上調**：使用者實測回報「精英怪跟 Boss 有點太弱」。

    改的是**血、傷害、衝擊上限**三項，體型與掉落不動：
    - `hp` 14 → 18：精英要撐得過玩家一輪齊射。舊值在第 10 分鐘之後
      （玩家清場速度約 40 隻/秒）等於一隻會發光的雜魚，「Boss 級遭遇」讀不出來
    - `dmg` 1.4 → 1.8：它要有「不能站著跟它對打」的壓力，否則玩家沒有理由走位
    - `impact` 90 → 110：血量拉高之後，舊的上限會讓它幾乎一被打就踉蹌 ——
      踉蹌窗口太便宜，集中火力的取捨就消失了（衝擊上限要對照「打死要幾發」訂，
      lessons 2026-08-01 記過反過來的那次）

    **這組數字是量出來的，不是估的。** 壓力診斷五顆種子
    （5EED1234 / A11CE / 7F3B2 / C0FFEE / 1D0A5，前後跑同一組）：

      A 舊值    hp14 dmg1.4 imp90    2.7 / 5.9 / 6.3 / 6.4 / 8.9   中位數 6.3
      D 現值    hp18 dmg1.8 imp110   3.0 / 3.8 / 4.9 / 7.5 / 8.2   中位數 4.9
      C 第一版  hp24 dmg2.0 imp140   2.2 / 2.8 / 3.7 / 4.5 / 12.0  中位數 3.7

    第一版（C）砍掉四成存活時間 —— 那不是「變強」是「開場就被淹掉」
    （密度那一輪記過同型的坑）。而 **`dmg` 幾乎不是槓桿**：把它從 2.0 降到 1.7
    中位數只從 3.7 動到 3.8，真正的槓桿是 `hp` 與 `impact`
    （精英活得久 → 場上同時六隻 → 壓力是累積的）。
    要再調的話動那兩個，不要動傷害。 */
export var ELITE = {
  hp: 18, dmg: 1.8, score: 10, xp: 12,
  scale: 1.34,         // 視覺與碰撞半徑一起放大（規格 §2.4）
  impact: 110          // 衝擊上限：雜魚是 0–34，精英拉到能踉蹌但要打一陣子
};

/** 第一組在 1:00，之後每 55 秒一組。與大型 Boss 同一分鐘時讓給 Boss。 */
export var ELITE_FIRST = 60;
export var ELITE_EVERY = 70;
/** 15 分之後的間隔倍率。**排程與診斷共用這一個常數** ——
    2026-08-06 改間隔時漏改了診斷那一份（排程 0.75、`elitesExpected` 還是 0.62），
    症狀會是「診斷說應該出 24 組、實際出 20 組」而程式完全正確。 */
export var ELITE_LATE_MUL = 0.75;

/** **同時在場的精英上限**。M12 把精英改成成組出現之後，
    「一次一隻」這個天然的上限就沒了 —— 玩家沒殺掉上一組、獵殺小隊又疊上來，
    場上會累積成一堵 14 倍血的牆，而那不是難度是死局。
    6 是這樣訂的：後期一組 4 隻，允許「上一組還剩一點」與新的一組重疊。 */
export var ELITE_MAX_ALIVE = 6;

export var ES = { next: 0, count: 0, squads: 0 };

export function resetElites() { ES.next = ELITE_FIRST; ES.count = 0; ES.squads = 0; }

/** 一組幾隻。跟著時間長 —— 第 15 分鐘還是一次一隻的話，
    精英在那個火力下只是個會發光的雜魚。 */
export function squadSize(t) {
  return t < 240 ? 1 : t < 600 ? 2 : t < 900 ? 3 : 4;
}

/** 目前場上活著的精英數（不含 Boss）。 */
export function eliteAlive(list) {
  var n = 0;
  for (var i = 0; i < list.length; i++) if (list[i].elite && list[i].alive) n++;
  return n;
}

/** 這一刻該不該出精英。`bossAt` 是大型 Boss 的時刻表，用來讓路。 */
export function eliteDue(t, bossAt) {
  if (t < ES.next) return false;
  for (var i = 0; i < bossAt.length; i++) {
    /* 讓路：大型 Boss 前後 8 秒不出精英。兩隻同時降臨的話畫面上會有
       兩條血條與兩個警告橫幅，玩家分不出哪個是重點。 */
    if (Math.abs(bossAt[i] - t) < 8) { ES.next = t + 12; return false; }
  }
  return true;
}

/** 精英出哪一種。愈後面愈重 —— 針蜂精英在第 15 分鐘只是個笑話。

    支援型（FÜHLER）**不當精英**：14 倍血的加成源等於一個打不掉的
    全場增益，那不是「硬」是「無解」。它該是玩家優先處理的目標，
    所以要維持「看到就打得掉」。 */
export function eliteType(t) {
  var pool = t < 240 ? ['nadel', 'lanze']
    : t < 600 ? ['lanze', 'nadel', 'geissel']
      : ['geissel', 'lanze', 'nadel'];
  return pool[Math.floor(RNG.next() * pool.length) % pool.length];
}

/** 把一隻普通敵人升格成精英。在 `spawnEnemy` 設定完基本欄位之後呼叫。 */
export function makeElite(e) {
  e.elite = true;
  /* 圖鑑：**精英是另一個解鎖條目**。`spawnEnemy` 已經記過一般版的「看過」，
     這裡再記精英版的 —— 兩者各自走「看過→剪影、打死→完整」。 */
  logSeen(eliteKey(e.type));
  e.hpMax *= ELITE.hp; e.hp = e.hpMax;
  e.dmgMul *= ELITE.dmg;
  e.r *= ELITE.scale;
  // 乘上去而不是指派 —— 這隻可能是「突刺型」變體（體型已經 ×1.14）
  e.eliteScale = (e.eliteScale || 1) * ELITE.scale;
  /* 提高衝擊上限而不是免疫踉蹌。VS 的 boss 對部分控場免疫，
     但踉蹌是這款遊戲的核心回饋 —— 拿掉它等於讓精英變成一塊沒有反應的肉。 */
  e.impCap = ELITE.impact;
  /* 發光：把第二種材質的自發光拉高。materials 是每隻共用的（同一個池），
     所以改的是 instance 自己的 mats[1]，不是 ETYPE 上的東西。 */
  if (e.mats && e.mats[1]) e.mats[1].emissiveIntensity = 3.6;
  /* 小怪的精英標記是 Field 那顆**全場共用的冠環**（規格 §2.4：
     28 種精英共用同一組，讓「精英」變成一個學得起來的符號，
     而不是 28 套要各學一次的造型）。它由 `o.crown` 開關，這裡不用做事。

     Boss 級的舊標記（金環 + 光柱）還在，因為它掛在真 rig 上。
     標記物件建一次就留著（跟著池化的敵人走），之後只切可見性 ——
     每次升格都 new 一個的話，同一個池物件用個幾十次就累積幾十個 Group。 */
  if (!e.T.fam) {
    if (!e.eliteMark) { e.eliteMark = makeEliteMarker(ETYPE[e.type].r); e.root.add(e.eliteMark.g); }
    e.eliteMark.g.visible = true;
    e.eliteMark.g.scale.setScalar(1);
  }
  /* 單獨升格（測試、除錯主控台）的精英自己就是組長 —— 掉寶箱。
     成組時由 `spawnEliteSquad` 覆寫，只有第一隻是組長。 */
  e.eliteLead = true;
  /* 精英改用真正的 rig：它要每隻不同的發光與標記，實例化給不了。
     升格前這隻是被實例化畫出來的（不在場景裡），所以這裡要真的加進去。 */
  attachEnemy(e);
  ES.count++;
  return e;
}

/** 成組降臨。**M12 之前是「一次一隻」** —— 而一隻精英在中後期的火力下
    只是一個多花三秒的路障，玩家根本不會改變打法。
    成組之後它變成一個需要處理的局面：先打哪一隻、要不要拉開、
    什麼時候用大招都變成問題。

    `a` 是來襲方位（獵殺小隊那一波要與預告的方向一致）；不給就自己抽。
    回傳實際放出來的隻數 —— **可能少於 `squadSize`**，因為場上有上限。 */
export function spawnEliteSquad(t, a?, named?) {
  var room = ELITE_MAX_ALIVE - eliteAlive(enemies);
  var n = Math.min(squadSize(t), room);
  if (n <= 0) return 0;
  var ang = a === undefined ? rr(0, TAU) : a;
  var kind = eliteType(t), got = 0;
  for (var i = 0; i < n; i++) {
    /* 同一個方位、散開一點：玩家要讀得出「那是一組」而不是「四個各自出現的」。 */
    var f = (n === 1 ? 0 : (i / (n - 1) - 0.5)) * 0.5;
    var d = 26 + (i % 2) * 3;
    var e = spawnEnemy(kind, P.pos.x + Math.cos(ang + f) * d, P.pos.z + Math.sin(ang + f) * d);
    if (!e) continue;
    makeElite(e);
    /* **一組一個寶箱**，掛在組長身上。跟著隻走的話寶箱數會翻倍以上，
       而寶箱是合成品的唯一來源 —— 實測會把該進化的武裝提前吃掉。
       組長的標記放大一圈：玩家要看得出「打哪一隻才有東西」。 */
    e.eliteLead = got === 0;
    /* ⚠️ **一組一個銀箱，不要再砍**（2026-08-06，砍過又退回來）。

       當天曾經改成「每兩組才掉一個」，理由是使用者說「寶箱太太太多了」。
       十顆種子的量測打臉：開箱次數 54 → **12**、零質變 **8/10 場**、
       決策總量 74.5 → 26.5、存活中位數 15.5 → 7.1 分，通關 0/10。

       真因是**「寶箱太多」本身是滿部件的體感** —— 使用者開得到 54 次
       是因為他活到 20 分鐘（當時局外部件是 ×4.11 的等效戰力），
       而零部件的玩家一場只開得到 12 次。銀箱同時是質變 Lv1 的主要來源
       （金箱只有 5 個），砍它等於同時砍掉成長與 build 的轉捩點。

       要再減寶箱的話**只動銅箱**（`sim/chest.ts` 的 `trashCdAt`，
       它是分段的而且 `craft: false`），不要動這一條。 */
    if (e.eliteLead && e.eliteMark) e.eliteMark.g.scale.setScalar(1.35);
    got++;
  }
  if (!got) return 0;
  ES.squads++;
  var cn = ETYPE[kind].cn || kind;
  banner(got > 1 ? '獵殺小隊 · ' + cn + ' ×' + got : '精英信號 · ' + cn);
  if (!named) radio(got > 1 ? '精英編組升空 · ' + got + ' 機訊號同步接近' : '單機精英訊號');
  SFX.alarm();
  return got;
}

/* 精英死亡的掉落與演出**故意不放在這裡**：`fx.ts` 的 killEnemy 會處理。
   放這裡的話 elites → fx（dropChest）與 fx → elites（onEliteKilled）
   會形成循環 import。這個檔只負責「什麼時候出、出什麼、升格成什麼」。 */

/** 排下一組。與 `eliteDue` 分開，讓「決定要出」與「排下一次」不會互相干擾。
    `blocked` 是「因為場上精英已經滿了而沒放成」—— 那種情況只推遲一小段，
    不然玩家清完場之後要再等一整個週期才有下一組。 */
export function scheduleNextElite(t, blocked?) {
  if (blocked) { ES.next = t + 12; return; }
  ES.next = t + ELITE_EVERY;
  /* 後段縮短間隔。VS 在 20:00 之後幾乎每分鐘都有 boss，而且常常一分鐘兩隻。 */
  if (t > 900) ES.next = t + ELITE_EVERY * ELITE_LATE_MUL;
}

export function eliteCount() { return ES.count; }

/** 給診斷用：這一刻理論上應該已經出過幾**組**精英（不是幾隻）。 */
export function elitesExpected(t) {
  if (t < ELITE_FIRST) return 0;
  return 1 + Math.floor((Math.min(t, 900) - ELITE_FIRST) / ELITE_EVERY) +
    (t > 900 ? Math.floor((t - 900) / (ELITE_EVERY * ELITE_LATE_MUL)) : 0);
}

/** 目前是否有精英在場（HUD 要不要顯示精英血條）。 */
export function anyElite(list) {
  for (var i = 0; i < list.length; i++) if (list[i].elite) return list[i];
  return null;
}
